import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABELS = {
  welcome: "Welcome",
  crm_followup: "Follow-up",
  speed_to_lead: "Speed-to-Lead",
  review_reply: "Review Reply",
};

export const AdminOutbound = () => {
  const [messages, setMessages] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => axios.get(`${API}/portal/admin/outbound`, { withCredentials: true })
    .then((r) => {
      setMessages(r.data.messages);
      setEdits((prev) => ({
        ...Object.fromEntries(r.data.messages.map((m) => [m.id, m.ai_text])), ...prev,
      }));
    }).catch(() => setMessages([]));

  useEffect(() => { load(); }, []);

  const approve = async (m) => {
    setBusy(m.id);
    try {
      const r = await axios.post(`${API}/portal/admin/outbound/${m.id}/approve`, { edited_text: edits[m.id] }, { withCredentials: true });
      toast.success(r.data.status === "approved" ? "Reply finalized — ready to post on Google." : `Sent to ${m.client_email}.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed.");
    } finally {
      setBusy(null);
    }
  };

  const regenerate = async (m) => {
    setBusy(m.id);
    try {
      const r = await axios.post(`${API}/portal/admin/outbound/${m.id}/regenerate`, {}, { withCredentials: true });
      setEdits({ ...edits, [m.id]: r.data.ai_text });
      toast.success("New draft generated.");
    } catch {
      toast.error("Regenerate failed.");
    } finally {
      setBusy(null);
    }
  };

  if (messages === null) return null;

  return (
    <div className="mt-10" data-testid="admin-outbound">
      <p className="font-mono-label text-xs uppercase tracking-[0.3em] text-slate-400 mb-2">AI Drafts — Approval Queue</p>
      <p className="text-sm text-slate-500 mb-5">
        Drafted automatically by the onboarding, follow-up, speed-to-lead and review agents. Review and edit first — emails send only on approval, and review replies are finalized for you to post on Google.
      </p>
      {messages.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="outbound-empty">No drafts waiting.</p>
      ) : (
        <div className="space-y-4">
          {messages.map((m) => (
            <div key={m.id} className={`rounded-2xl border p-5 ${m.flagged ? "border-amber-400/40" : "border-white/10"}`} data-testid={`outbound-card-${m.id.slice(0, 8)}`}>
              <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">
                {KIND_LABELS[m.kind] || m.kind} → {m.client_name}{m.client_email ? ` · ${m.client_email}` : ""}
              </p>
              {m.flagged && (
                <p className="mb-3 inline-block rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-300" data-testid={`outbound-flag-${m.id.slice(0, 8)}`}>
                  Negative review — needs your personal attention
                </p>
              )}
              {m.kind === "review_reply" && m.context && (
                <div className="mb-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3" data-testid={`outbound-context-${m.id.slice(0, 8)}`}>
                  <p className="text-xs text-amber-300 tracking-wide mb-1">
                    {"★".repeat(m.context.rating || 0)}{"☆".repeat(5 - (m.context.rating || 0))}
                    <span className="ml-2 text-slate-400">{m.context.business}</span>
                  </p>
                  {m.context.review_text && (
                    <p className="text-xs text-slate-400 italic leading-relaxed">“{m.context.review_text}”</p>
                  )}
                </div>
              )}
              {m.kind === "crm_followup" && m.context?.days_inactive && (
                <p className="mb-3 text-xs text-slate-500" data-testid={`outbound-context-${m.id.slice(0, 8)}`}>
                  No activity for {m.context.days_inactive} days{m.context.phone ? ` · ${m.context.phone}` : ""}
                </p>
              )}
              {m.kind === "speed_to_lead" && (m.context?.source || m.context?.phone) && (
                <p className="mb-3 text-xs text-slate-500" data-testid={`outbound-context-${m.id.slice(0, 8)}`}>
                  {m.context.source ? `Via ${m.context.source}` : ""}{m.context.source && m.context.phone ? " · " : ""}{m.context.phone || ""}
                </p>
              )}
              <p className="text-xs text-slate-500 mb-2">Subject: <span className="text-slate-300">{m.subject}</span></p>
              <textarea
                rows={6}
                value={edits[m.id] ?? m.ai_text}
                onChange={(e) => setEdits({ ...edits, [m.id]: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white leading-relaxed outline-none focus:border-white/40 resize-y"
                data-testid={`outbound-text-${m.id.slice(0, 8)}`}
              />
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => approve(m)} disabled={busy === m.id}
                  data-testid={`outbound-approve-${m.id.slice(0, 8)}`}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300 disabled:opacity-50"
                >
                  {busy === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {m.channel === "review" ? "Approve — Ready to Post" : "Approve & Send Email"}
                </button>
                <button
                  onClick={() => regenerate(m)} disabled={busy === m.id}
                  data-testid={`outbound-regen-${m.id.slice(0, 8)}`}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-colors duration-300 hover:bg-white hover:text-[#060D1A] disabled:opacity-50"
                >
                  <RefreshCw className="h-4 w-4" /> Regenerate
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
